import React, { useState, useEffect, useRef } from "react";
import { Result } from "../../types";
import PDFExport from "./InteractivePDFExport";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Bar, Doughnut, Radar } from "react-chartjs-2";
import {
  DataObject,
  Speed,
  Analytics,
  LocalActivity,
  AutoAwesome,
  Settings,
  Chat,
  Timeline,
  Campaign,
  Build,
  Celebration,
  Assignment,
  AttachMoney,
  Business,
  GpsFixed,
  Assessment,
  Code,
  SyncAlt,
  AccountBalance,
  Warehouse,
  Dashboard,
  Security,
  Schedule,
} from "@mui/icons-material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
);

interface ResultsPageProps {
  results: Result[];
  onRestart: () => void;
}

const ResultsPage: React.FC<ResultsPageProps> = ({ results, onRestart }) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [selectedTab, setSelectedTab] = useState("recommendations");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationStep((prev) => (prev < 4 ? prev + 1 : prev));
    }, 500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update recommendations generation function to better match questions
  const generateDetailedRecommendations = () => {
    const recommendations = [];
    const answers = results.map((r) => r.answer.toLowerCase());

    // Customer Data Sync
    if (
      answers.some(
        (a) =>
          a.includes("customer") ||
          a.includes("syncing") ||
          a.includes("website") ||
          a.includes("crm")
      )
    ) {
      recommendations.push({
        category: "Customer Data Sync",
        title: "CRM-Website Integration",
        description:
          "Seamlessly connect your website forms, customer interactions, and CRM system for unified customer data management.",
        priority: "High",
        icon: <SyncAlt />,
        benefits: [
          "Automatic customer data synchronization",
          "Eliminate duplicate data entry",
          "Real-time customer profile updates",
          "Unified customer journey tracking",
        ],
        implementation: "3-4 weeks",
        roi: "200% within 4 months",
        score: 95,
      });
    }

    // E-commerce Integration
    if (
      answers.some(
        (a) =>
          a.includes("sales") ||
          a.includes("order") ||
          a.includes("pos") ||
          a.includes("accounting")
      )
    ) {
      recommendations.push({
        category: "E-commerce Integration",
        title: "Sales & Accounting Connector",
        description:
          "Connect your POS, e-commerce platform, and accounting software for seamless financial data flow.",
        priority: "High",
        icon: <AccountBalance />,
        benefits: [
          "Automatic transaction recording",
          "Real-time inventory updates",
          "Streamlined tax reporting",
          "Reduced accounting errors by 90%",
        ],
        implementation: "4-6 weeks",
        roi: "180% within 6 months",
        score: 92,
      });
    }

    // Inventory Management
    if (
      answers.some(
        (a) =>
          a.includes("inventory") ||
          a.includes("warehouse") ||
          a.includes("stock")
      )
    ) {
      recommendations.push({
        category: "Inventory Management",
        title: "Warehouse-Order System Bridge",
        description:
          "Link your inventory management, warehouse operations, and order fulfillment systems for optimal stock control.",
        priority: "Medium",
        icon: <Warehouse />,
        benefits: [
          "Real-time inventory visibility",
          "Automated reorder triggers",
          "Prevent stockouts and overstock",
          "Optimize warehouse operations",
        ],
        implementation: "6-8 weeks",
        roi: "160% within 8 months",
        score: 88,
      });
    }

    // Real-time Monitoring
    if (
      answers.some(
        (a) =>
          a.includes("alerts") ||
          a.includes("dashboard") ||
          a.includes("real-time")
      )
    ) {
      recommendations.push({
        category: "Real-time Monitoring",
        title: "Live Dashboard & Alert System",
        description:
          "Create unified dashboards with real-time data from all connected systems and smart alerting capabilities.",
        priority: "Medium",
        icon: <Dashboard />,
        benefits: [
          "Single pane of glass visibility",
          "Instant anomaly detection",
          "Proactive issue resolution",
          "Data-driven decision making",
        ],
        implementation: "4-6 weeks",
        roi: "190% within 5 months",
        score: 85,
      });
    }

    // Data Automation
    if (
      answers.some(
        (a) =>
          a.includes("manual") ||
          a.includes("data entry") ||
          a.includes("reducing")
      )
    ) {
      recommendations.push({
        category: "Data Automation",
        title: "Automated Data Entry Elimination",
        description:
          "Remove manual data entry by creating intelligent data flows between all your business systems.",
        priority: "High",
        icon: <DataObject />,
        benefits: [
          "Eliminate 95% of manual data entry",
          "Reduce human errors significantly",
          "Free up staff for value-added tasks",
          "Ensure data consistency across systems",
        ],
        implementation: "4-8 weeks",
        roi: "220% within 6 months",
        score: 94,
      });
    }

    // Real-time vs Batch Processing
    if (
      answers.some(
        (a) => a.includes("instant") || a.includes("when things change")
      )
    ) {
      recommendations.push({
        category: "Real-time Sync",
        title: "Instant Data Synchronization",
        description:
          "Enable instant data updates across all connected systems as soon as changes occur.",
        priority: "High",
        icon: <Speed />,
        benefits: [
          "Immediate data consistency",
          "Real-time business insights",
          "Instant customer experience updates",
          "Rapid response to market changes",
        ],
        implementation: "6-10 weeks",
        roi: "210% within 8 months",
        score: 91,
      });
    } else if (
      answers.some(
        (a) =>
          a.includes("daily") ||
          a.includes("scheduled") ||
          a.includes("batches")
      )
    ) {
      recommendations.push({
        category: "Batch Processing",
        title: "Scheduled Data Synchronization",
        description:
          "Set up automated batch processes to sync data between systems on your preferred schedule.",
        priority: "Medium",
        icon: <Schedule />,
        benefits: [
          "Flexible scheduling options",
          "Efficient bulk data processing",
          "Reduced system load during peak hours",
          "Cost-effective for large data volumes",
        ],
        implementation: "3-6 weeks",
        roi: "150% within 7 months",
        score: 78,
      });
    }

    // Error Handling based on concerns
    if (
      answers.some(
        (a) =>
          a.includes("mix-ups") || a.includes("mistakes") || a.includes("data")
      )
    ) {
      recommendations.push({
        category: "Error Handling",
        title: "Intelligent Error Management",
        description:
          "Advanced error detection, handling, and recovery systems to ensure reliable data flow.",
        priority: "Medium",
        icon: <Security />,
        benefits: [
          "Automatic error detection and recovery",
          "Data integrity protection",
          "Minimal downtime during issues",
          "Comprehensive audit trails",
        ],
        implementation: "4-6 weeks",
        roi: "170% within 6 months",
        score: 86,
      });
    }

    // No-Code Platform based on ease of use preference
    if (
      answers.some((a) => a.includes("ease of use") || a.includes("simple"))
    ) {
      recommendations.push({
        category: "No-Code Platform",
        title: "Visual Integration Builder",
        description:
          "Drag-and-drop integration platform that enables business users to create connections without coding.",
        priority: "Medium",
        icon: <Build />,
        benefits: [
          "No technical expertise required",
          "Rapid integration deployment",
          "Easy maintenance and updates",
          "Democratize integration capabilities",
        ],
        implementation: "3-4 weeks",
        roi: "165% within 5 months",
        score: 83,
      });
    }

    return recommendations;
  };

  const recommendations = generateDetailedRecommendations();
  const categories = Array.from(
    new Set(recommendations.map((r) => r.category))
  );

  // Remove this unused priorityData variable (around line 237):
  // const priorityData = {
  //   labels: [
  //     "High Priority (Immediate Implementation)",
  //     "Medium Priority (Phase 2)",
  //     "Low Priority (Future Consideration)",
  //   ],
  //   datasets: [
  //     {
  //       data: [
  //         recommendations.filter((r) => r.priority === "High").length,
  //         recommendations.filter((r) => r.priority === "Medium").length,
  //         recommendations.filter((r) => r.priority === "Low").length,
  //       ],
  //       backgroundColor: ["#ef4444", "#f59e0b", "#10b981"],
  //       borderWidth: 0,
  //       hoverOffset: 10,
  //     },
  //   ],
  // };

  // Keep the other chart data objects that are actually being used:
  const categoryData = {
    labels: categories,
    datasets: [
      {
        label: "Number of Recommended Features",
        data: categories.map(
          (cat) => recommendations.filter((r) => r.category === cat).length
        ),
        backgroundColor: "rgba(99, 102, 241, 0.8)",
        borderColor: "#6366f1",
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const businessImpactData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "Business Impact Score (0-100)",
        data: recommendations.map((r) => r.score),
        backgroundColor: "rgba(16, 185, 129, 0.3)",
        borderColor: "#10b981",
        pointBackgroundColor: "#10b981",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#10b981",
      },
    ],
  };

  const roiTimelineData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "Expected ROI (%)",
        data: recommendations.map((r) => {
          // Extract ROI percentage from strings like "150% within 6 months"
          const roiMatch = r.roi.match(/(\d+)%/);
          return roiMatch ? parseInt(roiMatch[1]) : 100;
        }),
        backgroundColor: "rgba(245, 158, 11, 0.6)",
        borderColor: "#f59e0b",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
      {
        label: "Implementation Score",
        data: recommendations.map((r) => r.score),
        backgroundColor: "rgba(16, 185, 129, 0.6)",
        borderColor: "#10b981",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Priority distribution based on actual user responses
  const responseBasedPriority = {
    labels: [
      "Immediate Impact (High ROI)",
      "Strategic Growth (Medium ROI)",
      "Future Enhancement (Lower ROI)",
    ],
    datasets: [
      {
        data: [
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue >= 150;
          }).length,
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue >= 120 && roiValue < 150;
          }).length,
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue < 120;
          }).length,
        ],
        backgroundColor: ["#ef4444", "#f59e0b", "#10b981"],
        borderWidth: 0,
        hoverOffset: 10,
      },
    ],
  };

  return (
    <div
      ref={resultsRef}
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        minHeight: "100vh",
        padding: isMobile ? "1rem" : "2rem",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Enhanced Header with responsive design */}
        <div
          className={animationStep >= 0 ? "fade-in" : ""}
          style={{
            textAlign: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
            color: "white",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? "2.5rem" : "4rem",
              marginBottom: "1rem",
            }}
          >
            <Celebration
              sx={{ fontSize: isMobile ? "2.5rem" : "4rem", color: "white" }}
            />
          </div>
          <h1
            style={{
              fontFamily: "var(--font-headline)",
              fontSize: isMobile
                ? "var(--font-size-headline-mobile)"
                : "var(--font-size-headline)",
              fontWeight: "800",
              marginBottom: "1rem",
              background:
                "linear-gradient(35deg,rgb(215, 227, 255) 0%,rgb(155, 188, 255) 30%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              lineHeight: "1.2",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            Your Personalized Integration Roadmap
          </h1>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: isMobile
                ? "var(--font-size-body-mobile)"
                : "var(--font-size-body)",
              opacity: 0.9,
              maxWidth: "700px",
              margin: "0 auto 2rem",
              lineHeight: "1.6",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            Based on your responses, we've created a comprehensive integration
            strategy tailored to your connectivity needs with detailed
            implementation guidance and ROI projections.
          </p>

          {/* Key Statistics - Responsive Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(auto-fit, minmax(200px, 1fr))",
              gap: isMobile ? "0.75rem" : "1rem",
              maxWidth: "800px",
              margin: "0 auto",
              padding: isMobile ? "0 0.5rem" : "0",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
              }}
            >
              <Assignment
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                {recommendations.length}
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Integration Features
              </div>
            </div>
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
              }}
            >
              <Speed
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                3-10 weeks
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Implementation
              </div>
            </div>
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
                gridColumn: isMobile ? "1 / -1" : "auto",
              }}
            >
              <AttachMoney
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                150%+
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Expected ROI within 6-12 months
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs - Responsive */}
        <div
          className={animationStep >= 1 ? "fade-in" : ""}
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
            opacity: animationStep >= 1 ? 1 : 0,
            transform:
              animationStep >= 1 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            flexWrap: "wrap",
            gap: isMobile ? "0.5rem" : "0",
          }}
        >
          {["recommendations", "analytics", "your-responses"].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              style={{
                padding: isMobile ? "0.75rem 1rem" : "1rem 2rem",
                margin: isMobile ? "0" : "0 0.5rem",
                backgroundColor:
                  selectedTab === tab
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(255, 255, 255, 0.1)",
                color: selectedTab === tab ? "#1f2937" : "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: isMobile ? "0.9rem" : "1.1rem",
                fontWeight: "600",
                transition: "all 0.3s ease",
                backdropFilter: "blur(10px)",
                textTransform: "capitalize",
                minWidth: isMobile ? "auto" : "120px",
              }}
            >
              {tab === "your-responses" ? "Your Responses" : tab}
            </button>
          ))}
        </div>

        {/* Content based on selected tab */}
        {selectedTab === "recommendations" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Detailed Recommendations - Responsive Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(400px, 1fr))",
                gap: isMobile ? "1.5rem" : "2rem",
                marginBottom: isMobile ? "2rem" : "3rem",
              }}
            >
              {recommendations.map((recommendation, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "20px",
                    padding: isMobile ? "1.5rem" : "2.5rem",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    transition: "transform 0.3s ease",
                    transitionDelay: `${index * 0.1}s`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "translateY(-8px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "translateY(0)";
                    }
                  }}
                >
                  {/* Priority Badge - Responsive */}
                  <div
                    style={{
                      position: "absolute",
                      top: "1rem",
                      right: "1rem",
                      padding: isMobile ? "0.3rem 0.75rem" : "0.5rem 1rem",
                      borderRadius: "20px",
                      fontSize: isMobile ? "0.7rem" : "0.8rem",
                      fontWeight: "600",
                      backgroundColor:
                        recommendation.priority === "High"
                          ? "#fee2e2"
                          : recommendation.priority === "Medium"
                          ? "#fef3c7"
                          : "#d1fae5",
                      color:
                        recommendation.priority === "High"
                          ? "#dc2626"
                          : recommendation.priority === "Medium"
                          ? "#d97706"
                          : "#059669",
                    }}
                  >
                    {recommendation.priority} Priority
                  </div>

                  {/* Header - Responsive */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      marginBottom: "1.5rem",
                      flexDirection: isMobile ? "column" : "row",
                      textAlign: isMobile ? "center" : "left",
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "2rem" : "3rem",
                        marginRight: isMobile ? "0" : "1rem",
                        marginBottom: isMobile ? "1rem" : "0",
                        color: "#c9a750ff",
                      }}
                    >
                      {React.cloneElement(recommendation.icon, {
                        sx: { fontSize: isMobile ? "2rem" : "3rem" },
                      })}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#c9a750ff",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        {recommendation.category}
                      </div>
                      <h3
                        style={{
                          color: "#1f2937",
                          margin: 0,
                          fontSize: isMobile ? "1.2rem" : "1.4rem",
                          lineHeight: "1.3",
                        }}
                      >
                        {recommendation.title}
                      </h3>
                    </div>
                  </div>

                  {/* Description */}
                  <p
                    style={{
                      color: "#6b7280",
                      lineHeight: "1.6",
                      marginBottom: "1.5rem",
                      fontSize: isMobile ? "0.9rem" : "1rem",
                    }}
                  >
                    {recommendation.description}
                  </p>

                  {/* Benefits - Responsive Grid */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <h4
                      style={{
                        color: "#1f2937",
                        marginBottom: "0.75rem",
                        fontSize: "1rem",
                      }}
                    >
                      Key Benefits:
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : "repeat(2, 1fr)",
                        gap: "0.5rem",
                      }}
                    >
                      {recommendation.benefits.map((benefit, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            fontSize: isMobile ? "0.8rem" : "0.9rem",
                            color: "#4b5563",
                          }}
                        >
                          <span
                            style={{ color: "#10b981", marginRight: "0.5rem" }}
                          >
                            ✓
                          </span>
                          {benefit}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Implementation Details - Responsive */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                      gap: "1rem",
                      padding: "1rem",
                      backgroundColor: "rgba(201, 167, 80, 0.1)",
                      borderRadius: "12px",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Timeline
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#1f2937",
                        }}
                      >
                        {recommendation.implementation}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Expected ROI
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#059669",
                        }}
                      >
                        {recommendation.roi}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Impact Score
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#c9a750ff",
                        }}
                      >
                        {recommendation.score}/100
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTab === "analytics" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Charts Grid - Responsive */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: isMobile ? "1.5rem" : "2rem",
                marginBottom: isMobile ? "2rem" : "3rem",
              }}
              className="analytics-container"
            >
              {/* System Connection Priorities - Horizontal Bar Chart */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
                data-chart="system-connections"
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <SyncAlt
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  System Connection Priorities
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Priority systems to connect based on your responses
                </p>

                <Bar
                  data={{
                    labels: [
                      "Website+CRM",
                      "Sales+Accounting",
                      "Inventory+Orders",
                      "Other Tools",
                    ],
                    datasets: [
                      {
                        label: "Connection Priority Score",
                        data: [
                          // Calculate scores based on user answers
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("website") ||
                              r.answer.toLowerCase().includes("crm")
                          )
                            ? 90
                            : 40,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("sales") ||
                              r.answer.toLowerCase().includes("accounting") ||
                              r.answer.toLowerCase().includes("pos")
                          )
                            ? 85
                            : 35,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("inventory") ||
                              r.answer.toLowerCase().includes("warehouse")
                          )
                            ? 80
                            : 30,
                          results.some((r) =>
                            r.answer.toLowerCase().includes("other tools")
                          )
                            ? 70
                            : 25,
                        ],
                        backgroundColor: [
                          "#3b82f6", // Blue for Website+CRM
                          "#10b981", // Green for Sales+Accounting
                          "#f59e0b", // Orange for Inventory+Orders
                          "#8b5cf6", // Purple for Other Tools
                        ],
                        borderWidth: 0,
                        borderRadius: 8,
                      },
                    ],
                  }}
                  options={{
                    indexAxis: "y" as const,
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `Priority Score: ${context.parsed.x}/100`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                        max: 100,
                        grid: { display: false },
                      },
                      y: {
                        grid: { display: false },
                      },
                    },
                  }}
                />
              </div>

              {/* Data Transfer Frequency - Doughnut Chart */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
                data-chart="data-transfer-frequency"
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Speed
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Data Transfer Preferences
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Your preferred data synchronization patterns
                </p>

                <Doughnut
                  data={{
                    labels: [
                      "Real-time Sync",
                      "Daily/Scheduled",
                      "Periodic Reports",
                      "Custom Timing",
                    ],
                    datasets: [
                      {
                        data: [
                          // Calculate percentages based on user preferences
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("instant") ||
                              r.answer.toLowerCase().includes("real-time") ||
                              r.answer
                                .toLowerCase()
                                .includes("when things change")
                          )
                            ? 40
                            : 15,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("daily") ||
                              r.answer.toLowerCase().includes("scheduled") ||
                              r.answer.toLowerCase().includes("batches")
                          )
                            ? 35
                            : 15,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("periodic") ||
                              r.answer.toLowerCase().includes("weekly") ||
                              r.answer.toLowerCase().includes("monthly")
                          )
                            ? 25
                            : 15,
                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("custom") ||
                              r.answer.toLowerCase().includes("friday")
                          )
                            ? 20
                            : 15,
                        ],
                        backgroundColor: [
                          "#ef4444", // Red for Real-time
                          "#3b82f6", // Blue for Daily/Scheduled
                          "#f59e0b", // Orange for Periodic
                          "#10b981", // Green for Custom
                        ],
                        borderWidth: 0,
                        hoverOffset: 10,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: "bottom" as const,
                        labels: {
                          usePointStyle: true,
                          padding: isMobile ? 15 : 20,
                          font: {
                            size: isMobile ? 10 : 12,
                          },
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            const total = context.dataset.data.reduce(
                              (a: number, b: number) => a + b,
                              0
                            );
                            const percentage = Math.round(
                              (context.parsed / total) * 100
                            );
                            return `${context.label}: ${percentage}%`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>

              {/* Integration Readiness - Radar Chart */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
                data-chart="integration-readiness"
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Assessment
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Integration Readiness Assessment
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Your organization's readiness across key integration factors
                </p>

                <Radar
                  data={{
                    labels: [
                      "Automation Level",
                      "Technical Complexity",
                      "Change Urgency",
                      "Error Tolerance",
                      "Budget Readiness",
                      "System Flexibility",
                    ],
                    datasets: [
                      {
                        label: "Current State",
                        data: [
                          // Calculate readiness scores based on answers
                          results.some(
                            (r) =>
                              r.answer
                                .toLowerCase()
                                .includes("very—no manual") ||
                              r.answer.toLowerCase().includes("automatic")
                          )
                            ? 85
                            : results.some(
                                (r) =>
                                  r.answer
                                    .toLowerCase()
                                    .includes("some manual") ||
                                  r.answer.toLowerCase().includes("mix")
                              )
                            ? 65
                            : 45,

                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("ease of use") ||
                              r.answer.toLowerCase().includes("simple")
                          )
                            ? 85
                            : results.some(
                                (r) =>
                                  r.answer.toLowerCase().includes("flexible") ||
                                  r.answer.toLowerCase().includes("complex")
                              )
                            ? 45
                            : 65,

                          results.some((r) =>
                            r.answer.toLowerCase().includes("within a week")
                          )
                            ? 95
                            : results.some((r) =>
                                r.answer.toLowerCase().includes("2-4 weeks")
                              )
                            ? 80
                            : results.some((r) =>
                                r.answer.toLowerCase().includes("few months")
                              )
                            ? 50
                            : 25,

                          results.some((r) =>
                            r.answer
                              .toLowerCase()
                              .includes("instant notification")
                          )
                            ? 90
                            : results.some((r) =>
                                r.answer.toLowerCase().includes("daily summary")
                              )
                            ? 70
                            : results.some((r) =>
                                r.answer.toLowerCase().includes("manually")
                              )
                            ? 40
                            : 60,

                          results.some((r) =>
                            r.answer.toLowerCase().includes("$500+")
                          )
                            ? 90
                            : results.some(
                                (r) =>
                                  r.answer.toLowerCase().includes("$200") ||
                                  r.answer.toLowerCase().includes("$500")
                              )
                            ? 70
                            : results.some(
                                (r) =>
                                  r.answer.toLowerCase().includes("$100") ||
                                  r.answer.toLowerCase().includes("$200")
                              )
                            ? 50
                            : 30,

                          results.some(
                            (r) =>
                              r.answer.toLowerCase().includes("expert-level") ||
                              r.answer.toLowerCase().includes("highly flexible")
                          )
                            ? 95
                            : results.some((r) =>
                                r.answer.toLowerCase().includes("bit flexible")
                              )
                            ? 75
                            : results.some((r) =>
                                r.answer.toLowerCase().includes("ease of use")
                              )
                            ? 45
                            : 65,
                        ],
                        backgroundColor: "rgba(16, 185, 129, 0.2)",
                        borderColor: "#10b981",
                        pointBackgroundColor: "#10b981",
                        pointBorderColor: "#fff",
                        pointHoverBackgroundColor: "#fff",
                        pointHoverBorderColor: "#10b981",
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    scales: {
                      r: {
                        beginAtZero: true,
                        max: 100,
                        ticks: {
                          stepSize: 20,
                        },
                      },
                    },
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                  }}
                />
              </div>

              {/* Integration Value Matrix - Custom Bubble Chart */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
                data-chart="integration-value-matrix"
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Timeline
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Integration Value Matrix
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Integration features mapped by business value vs
                  implementation effort
                </p>

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "20px",
                    height: "250px",
                    position: "relative",
                    marginBottom: "10px",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  {/* Axis labels */}
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      textAlign: "center",
                      fontWeight: "bold",
                      color: "#6b7280",
                      fontSize: "0.8rem",
                      padding: "5px",
                    }}
                  >
                    Implementation Effort →
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: 0,
                      display: "flex",
                      alignItems: "center",
                      transform: "rotate(-90deg) translateX(-50%)",
                      transformOrigin: "left center",
                      fontWeight: "bold",
                      color: "#6b7280",
                      fontSize: "0.8rem",
                    }}
                  >
                    ← Business Value
                  </div>

                  {/* Quadrant labels */}
                  <div
                    style={{
                      position: "absolute",
                      top: "15%",
                      left: "20%",
                      transform: "translate(-50%, -50%)",
                      fontSize: "0.7rem",
                      color: "#059669",
                      fontWeight: "bold",
                    }}
                  >
                    Quick Wins
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: "15%",
                      right: "20%",
                      transform: "translate(50%, -50%)",
                      fontSize: "0.7rem",
                      color: "#d97706",
                      fontWeight: "bold",
                    }}
                  >
                    Major Projects
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "15%",
                      left: "20%",
                      transform: "translate(-50%, 50%)",
                      fontSize: "0.7rem",
                      color: "#6b7280",
                    }}
                  >
                    Fill-ins
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "15%",
                      right: "20%",
                      transform: "translate(50%, 50%)",
                      fontSize: "0.7rem",
                      color: "#dc2626",
                    }}
                  >
                    Questionable
                  </div>

                  {/* Quadrant dividers */}
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: 0,
                      right: 0,
                      borderTop: "2px dashed #d1d5db",
                    }}
                  ></div>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: "50%",
                      borderLeft: "2px dashed #d1d5db",
                    }}
                  ></div>

                  {/* Plot integration features */}
                  {recommendations.map((rec, index) => {
                    // Business value based on score and user preferences
                    const businessValue = rec.score;

                    // Implementation effort based on timeline and complexity
                    const effort = rec.implementation.includes("1-2")
                      ? 20
                      : rec.implementation.includes("2-3")
                      ? 35
                      : rec.implementation.includes("3-4")
                      ? 50
                      : rec.implementation.includes("4-8")
                      ? 75
                      : 60;

                    // Position: business value (y-axis inverted) vs effort (x-axis)
                    const top = 100 - businessValue; // Invert Y so high value is at top
                    const left = effort;

                    // Size based on ROI
                    const roiMatch = rec.roi.match(/(\d+)%/);
                    const roiValue = roiMatch ? parseInt(roiMatch[1]) : 150;
                    const size = Math.max(
                      30,
                      Math.min(60, (roiValue - 100) / 2 + 30)
                    );

                    // Color based on priority
                    const color =
                      rec.priority === "High"
                        ? "#059669"
                        : rec.priority === "Medium"
                        ? "#d97706"
                        : "#6b7280";

                    return (
                      <div
                        key={index}
                        style={{
                          position: "absolute",
                          top: `${Math.min(Math.max(top, 10), 90)}%`,
                          left: `${Math.min(Math.max(left, 10), 90)}%`,
                          width: `${size}px`,
                          height: `${size}px`,
                          borderRadius: "50%",
                          backgroundColor: `${color}99`,
                          border: `2px solid ${color}`,
                          transform: "translate(-50%, -50%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontSize: "10px",
                          fontWeight: "bold",
                          zIndex: 2,
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                        }}
                        title={`${rec.title}\nBusiness Value: ${businessValue}/100\nEffort: ${effort}%\nROI: ${roiValue}%`}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform =
                            "translate(-50%, -50%) scale(1.2)";
                          e.currentTarget.style.zIndex = "10";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform =
                            "translate(-50%, -50%) scale(1)";
                          e.currentTarget.style.zIndex = "2";
                        }}
                      >
                        {index + 1}
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    color: "#6b7280",
                  }}
                >
                  Bubble size indicates ROI potential • Numbers correspond to
                  recommendations above
                </div>
              </div>
            </div>

            {/* Chart Explanations - Updated for Integration */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "20px",
                padding: isMobile ? "1.5rem" : "2rem",
                backdropFilter: "blur(10px)",
                marginBottom: "2rem",
              }}
            >
              <h3
                style={{
                  marginBottom: "1.5rem",
                  color: "#1f2937",
                  fontSize: isMobile ? "1.2rem" : "1.3rem",
                }}
              >
                📖 Understanding Your Integration Analytics
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "1.5rem",
                }}
              >
                <div>
                  <h4
                    style={{
                      color: "#c9a750ff",
                      marginBottom: "0.5rem",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                    }}
                  >
                    🔗 System Connection Priorities
                  </h4>
                  <p
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    Shows which system integrations are most important based on
                    your responses. Higher scores indicate systems that should
                    be connected first for maximum impact.
                  </p>
                </div>
                <div>
                  <h4
                    style={{
                      color: "#c9a750ff",
                      marginBottom: "0.5rem",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                    }}
                  >
                    ⚡ Data Transfer Preferences
                  </h4>
                  <p
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    Your preferred data synchronization patterns. Real-time sync
                    provides instant updates, while scheduled transfers are more
                    resource-efficient for large data volumes.
                  </p>
                </div>
                <div>
                  <h4
                    style={{
                      color: "#c9a750ff",
                      marginBottom: "0.5rem",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                    }}
                  >
                    📊 Integration Readiness
                  </h4>
                  <p
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    Assessment of your organization's readiness across key
                    factors. Higher scores indicate areas where you're
                    well-prepared for integration projects.
                  </p>
                </div>
                <div>
                  <h4
                    style={{
                      color: "#c9a750ff",
                      marginBottom: "0.5rem",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                    }}
                  >
                    🎯 Integration Value Matrix
                  </h4>
                  <p
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    Quick Wins (top-left) offer high value with low effort.
                    Major Projects (top-right) require more resources but
                    deliver significant value. Focus on Quick Wins first.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Your Responses section would follow similar responsive patterns */}
        {selectedTab === "your-responses" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Response content with mobile responsiveness */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "20px",
                padding: isMobile ? "1.5rem" : "2.5rem",
                marginBottom: "2rem",
                backdropFilter: "blur(10px)",
              }}
            >
              {/* Response Summary Stats - Mobile responsive grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "repeat(2, 1fr)"
                    : "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "1rem",
                  marginBottom: "2rem",
                  padding: isMobile ? "1rem" : "1.5rem",
                  backgroundColor: "rgba(201, 167, 80, 0.1)",
                  borderRadius: "12px",
                }}
              >
                {/* Stats cards with responsive text sizes */}
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#c9a750ff",
                    }}
                  >
                    📊
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {results.length}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    Questions Answered
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#10b981",
                    }}
                  >
                    ⚡
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {
                      recommendations.filter((r) => r.priority === "High")
                        .length
                    }
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    High Priority Features
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#f59e0b",
                    }}
                  >
                    🎯
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {Math.round(
                      recommendations.reduce((acc, r) => acc + r.score, 0) /
                        recommendations.length
                    )}
                    %
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    Avg. Match Score
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#8b5cf6",
                    }}
                  >
                    💼
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {categories.length}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    Business Areas Covered
                  </div>
                </div>
              </div>

              {/* Individual responses with mobile optimization */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: isMobile ? "1rem" : "1.5rem",
                }}
              >
                {results.map((result, index) => (
                  <div
                    key={index}
                    style={{
                      padding: isMobile ? "1.5rem" : "2rem",
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      borderRadius: "16px",
                      border: "1px solid rgba(201, 167, 80, 0.2)",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    {/* Question header with responsive layout */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: "1.5rem",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#c9a750ff",
                          color: "white",
                          borderRadius: "50%",
                          width: isMobile ? "28px" : "32px",
                          height: isMobile ? "28px" : "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          fontWeight: "600",
                          marginRight: isMobile ? "0" : "1rem",
                          marginBottom: isMobile ? "1rem" : "0",
                          flexShrink: 0,
                          alignSelf: isMobile ? "center" : "flex-start",
                        }}
                      >
                        Q{index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h5
                          style={{
                            fontWeight: "700",
                            color: "#1f2937",
                            marginBottom: "0.5rem",
                            fontSize: isMobile ? "1rem" : "1.1rem",
                            lineHeight: "1.4",
                            textAlign: isMobile ? "center" : "left",
                          }}
                        >
                          {result.question}
                        </h5>
                      </div>
                    </div>

                    {/* Answer and impact sections with responsive styling */}
                    <div
                      style={{
                        backgroundColor: "rgba(201, 167, 80, 0.1)",
                        padding: isMobile ? "1rem" : "1.25rem",
                        borderRadius: "12px",
                        borderLeft: "4px solid #c9a750ff",
                        marginBottom: "1rem",
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Your Response:
                      </div>
                      <div
                        style={{
                          color: "#c9a750ff",
                          fontWeight: "600",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {result.answer}
                      </div>
                    </div>

                    {/* Impact Analysis */}
                    <div
                      style={{
                        backgroundColor: "rgba(16, 185, 129, 0.05)",
                        padding: isMobile ? "1rem" : "1.25rem",
                        borderRadius: "12px",
                        borderLeft: "4px solid #10b981",
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Impact on Recommendations:
                      </div>
                      <div
                        style={{
                          color: "#059669",
                          fontSize: isMobile ? "0.9rem" : "0.95rem",
                          lineHeight: "1.5",
                        }}
                      >
                        {/* Dynamic impact analysis based on the answer */}
                        {result.answer.toLowerCase().includes("data") ||
                        result.answer.toLowerCase().includes("entry")
                          ? "✓ Triggered data processing automation features for document handling and manual entry reduction"
                          : result.answer.toLowerCase().includes("workflow") ||
                            result.answer.toLowerCase().includes("process")
                          ? "✓ Recommended business process automation features for streamlined operations"
                          : result.answer.toLowerCase().includes("analytics") ||
                            result.answer.toLowerCase().includes("report")
                          ? "✓ Added advanced analytics and reporting automation capabilities to your recommendations"
                          : result.answer
                              .toLowerCase()
                              .includes("integration") ||
                            result.answer.toLowerCase().includes("connect")
                          ? "✓ Included system integration features for seamless data flow between applications"
                          : result.answer.toLowerCase().includes("no-code") ||
                            result.answer
                              .toLowerCase()
                              .includes("non-technical")
                          ? "✓ Focused on user-friendly no-code automation tools for your team"
                          : "✓ Contributed to your personalized automation feature selection and priority ranking"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons - Responsive */}
        <div
          style={{
            display: "flex",
            gap: isMobile ? "0.75rem" : "1rem",
            justifyContent: "center",
            flexWrap: "wrap",
            opacity: animationStep >= 3 ? 1 : 0,
            transform:
              animationStep >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            padding: isMobile ? "0 1rem" : "0",
          }}
        >
          <PDFExport
            results={results}
            recommendations={recommendations}
            resultsRef={resultsRef}
          />
          <button
            onClick={onRestart}
            style={{
              padding: isMobile ? "0.75rem 1.5rem" : "1rem 2rem",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              border: "2px solid #c9a750ff",
              borderRadius: "12px",
              cursor: "pointer",
              fontSize: isMobile ? "1rem" : "1.1rem",
              fontWeight: "600",
              transition: "all 0.3s ease",
              backdropFilter: "blur(10px)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#c9a750ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.2)";
            }}
          >
            Take Quiz Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
